/**
 * dropdownOptions — the single source of truth for the page-content
 * dropdown-option builders (SCRUM-907). These pure `{label, value}` mappers
 * were previously copied verbatim across the three page-content hosts:
 *   - TemplateSelector/IndexTemplateSelector.jsx (create wizard)
 *   - UpdatePage/the former Required host (.jsx)
 *   - UpdatePage/the former Optional host (.jsx)
 *
 * Placed in the PagesComponent parent dir so both TemplateSelector/ and
 * UpdatePage/ import it without reaching into each other.
 *
 * SCOPE — pure shaping only. The per-screen DEVIATIONS stay in the hosts and
 * are intentionally NOT captured here (pinned by the characterization tests):
 *   - product SOURCE + narrowing: create/Optional read the productproperties
 *     slice; Required reads categories.subCategory_by_id; category /
 *     sub_category narrowing is host logic (see useProductStateHost.js
 *     selectProductOptions).
 *   - tag 'global' filter/inject and click-order vs options-order derivation
 *     live in the hosts' override maps and appliers.
 *
 * NOTE — the Category/Subcategory/Ad/Post mappings also exist in
 * variableFields/useVariableOptions.ts (the settings side). They are NOT
 * unified with these: different sources and their own tests. That residual
 * duplication is accepted and out of SCRUM-907's scope.
 *
 * Every builder tolerates null/undefined input (returns []) so call sites stay
 * guard-free. Each builder is generic over the doc it is given, so an option's
 * `value` keeps the caller's own type.
 */

/**
 * A `{label, value}` option as the react-select dropdowns take it. `label` is
 * whatever the doc carries, so it is undefined when the doc has no name/title.
 */
export type DropdownOption<T> = { label: string | undefined; value: T };

/**
 * Product-property docs → options. Guarded variant (was HandleContent (required)'s
 * prepareProduts): a doc missing `product`/`product.title` is skipped with a
 * warning instead of producing a `{label: undefined}` option. Identical to the
 * create/Optional plain mappers on well-formed data (SCRUM-907 D2).
 */
export const toProductOptions = <T extends { product?: { title?: string } }>(
  products?: T[] | null
): DropdownOption<T>[] =>
  (products ?? []).flatMap((prod) => {
    if (prod.product && prod.product.title) {
      return [{ label: prod.product.title, value: prod }];
    }
    console.warn('Skipped product due to missing "product" or "title":', prod);
    return [];
  });

/**
 * Categories → options.
 */
export const toCategoryOptions = <T extends { name?: string }>(categories?: T[] | null): DropdownOption<T>[] =>
  (categories ?? []).map((cat) => ({ label: cat.name, value: cat }));

/**
 * Categories → flattened sub-category options, labelled "sub || category".
 */
export const toSubCategoryOptions = <S extends { name?: string }>(
  categories?: { name?: string; subcategories?: S[] }[] | null
): DropdownOption<S>[] =>
  (categories ?? []).flatMap((cat) =>
    (cat.subcategories ?? []).map((sub) => ({
      label: sub.name + " || " + cat.name,
      value: sub,
    }))
  );

/**
 * Posts → options: ACTIVE only, newest `updatedAt` first (`createdAt`
 * fallback, then 0). Non-mutating (copies before sort).
 */
export const toPostOptions = <
  T extends { active?: boolean; title?: string; updatedAt?: string | number | Date; createdAt?: string | number | Date },
>(
  posts?: T[] | null
): DropdownOption<T>[] =>
  [...(posts ?? [])]
    .filter((post) => post.active)
    .sort(
      (a, b) =>
        new Date(b.updatedAt || b.createdAt || 0).getTime() -
        new Date(a.updatedAt || a.createdAt || 0).getTime()
    )
    .map((post) => ({ label: post.title, value: post }));

/**
 * Pages → options for the internal-link picker (SCRUM-728): PUBLISHED only,
 * labelled "global_title || path" (the compound form toSubCategoryOptions
 * already uses), path alone when the page carries no title.
 *
 * Published is `publish_statement !== false`, not a truthy test: the field is
 * `required: false` on the model (server/models/page.js:18), so a document
 * that predates it has no flag at all and must not disappear from the picker.
 * That is the same rule the sibling pages dropdown applies host-side
 * (variableFields/CategoryMenuField.jsx:85, documented at :60). The two are
 * deliberately NOT unified — that host filters its own copy for its own
 * pageTypes derivation, and touching it is outside this ticket.
 *
 * Narrowing inside a builder follows toPostOptions above, which filters to
 * `active` for the same reason: a picker offers only what an editor may pick.
 *
 * The value is the whole page doc, like every builder above; callers read
 * `page_configuration.path` off it.
 */
export const toPageOptions = <
  T extends { publish_statement?: boolean; global_title?: string; page_configuration?: { path?: string } },
>(
  pages?: T[] | null
): DropdownOption<T>[] =>
  (pages ?? [])
    .filter((page) => page.publish_statement !== false)
    .map((page) => {
      const path = page.page_configuration?.path ?? "";
      return {
        label: page.global_title ? page.global_title + " || " + path : path,
        value: page,
      };
    });
